<template>
  <div class="user-profile-container">
    <div class="user-profile-header">
      <span class="back" @click="$router.push('/settings')">&#8592;</span>
      <span class="title">个人信息</span>
    </div>
    
    <div v-if="loading" class="loading-container">
      <div class="loading-text">加载中...</div>
    </div>
    
    <div v-else-if="error" class="error-container">
      <div class="error-text">{{ error }}</div>
      <button class="retry-btn" @click="loadUserInfo">重试</button>
    </div>
    
    <div v-else class="user-profile-content">
      <div class="profile-avatar-section">
        <div class="avatar-wrapper">
          <img v-if="userInfo.avatar" :src="userInfo.avatar" alt="头像" class="avatar-img" />
          <div v-else class="avatar-placeholder">{{ avatarText }}</div>
        </div>
        <div class="avatar-name">{{ displayName }}</div>
      </div>
      
      <div class="info-section">
        <div class="info-group">
          <div class="info-group-title">基本信息</div>
          <div class="info-item">
            <span class="info-label">用户名</span>
            <span class="info-value">{{ userInfo.username || '未设置' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">真实姓名</span>
            <span class="info-value">{{ userInfo.realName || '未设置' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">手机号</span>
            <span class="info-value">{{ userInfo.phone || '未绑定' }}</span>
            <span v-if="userInfo.phoneIsAuthed" class="auth-badge">已认证</span>
          </div>
        </div>
        
        <div v-if="userInfo.wxOpenId || userInfo.alipayOpenId" class="info-group">
          <div class="info-group-title">第三方账号</div>
          <div v-if="userInfo.wxOpenId" class="info-item">
            <span class="info-label">微信</span>
            <span class="info-value">{{ userInfo.wxNickname || '已绑定' }}</span>
          </div>
          <div v-if="userInfo.alipayOpenId" class="info-item">
            <span class="info-label">支付宝</span>
            <span class="info-value">{{ userInfo.alipayNickname || '已绑定' }}</span>
          </div>
        </div>
        
        <div v-if="userInfo.roles && userInfo.roles.length > 0" class="info-group">
          <div class="info-group-title">角色信息</div>
          <div class="roles-list">
            <span v-for="role in userInfo.roles" :key="role.roleId" class="role-tag">
              {{ role.roleName || role.name }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getUserInfo } from '@/api/user'

export default {
  name: 'UserProfilePage',
  data () {
    return {
      loading: true,
      error: null,
      userInfo: {}
    }
  },
  computed: {
    displayName () {
      return this.userInfo.realName || this.userInfo.wxNickname || this.userInfo.alipayNickname || this.userInfo.username || '用户'
    },
    avatarText () {
      const name = this.displayName
      if (name && name.length > 0) {
        return name.charAt(0).toUpperCase()
      }
      return '👤'
    }
  },
  mounted () {
    this.loadUserInfo()
  },
  methods: {
    async loadUserInfo () {
      this.loading = true
      this.error = null
      try {
        const response = await getUserInfo()
        // 后端返回格式可能是: { code, message, data: UserDTO } 或直接是 UserDTO
        // request 拦截器返回的是 response.data，所以这里 response 可能是 MyApiResponse 或 UserDTO
        const userData = response?.data || response
        this.userInfo = userData || {}
        console.log('获取到的用户信息:', this.userInfo)
      } catch (error) {
        console.error('获取用户信息失败:', error)
        // 处理错误信息
        if (error?.response?.data) {
          const errorData = error.response.data
          this.error = errorData.message || errorData.msg || '获取用户信息失败，请稍后重试'
        } else {
          this.error = error?.message || '获取用户信息失败，请稍后重试'
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.user-profile-container {
  width: 100vw;
  max-width: 430px;
  min-height: 100vh;
  background: #f7f8fa;
  margin: 0 auto;
  box-sizing: border-box;
  padding-bottom: 40px;
}

.user-profile-header {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-bottom: 1px solid #f2f2f2;
  position: relative;
  font-size: 20px;
  font-weight: bold;
}

.user-profile-header .back {
  position: absolute;
  left: 0;
  height: 56px;
  display: flex;
  align-items: center;
  font-size: 26px;
  padding-left: 16px;
  cursor: pointer;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.loading-text,
.error-text {
  font-size: 16px;
  color: #888;
  margin-bottom: 20px;
}

.error-text {
  color: #f56c6c;
}

.retry-btn {
  background: #3572e6;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;
}

.retry-btn:active {
  background: #2a5bc7;
}

.user-profile-content {
  padding: 20px 16px;
}

.profile-avatar-section {
  background: #fff;
  border-radius: 16px;
  padding: 32px 20px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 2px 8px #e0e0e033;
}

.avatar-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  margin-bottom: 16px;
  background: #eaf3ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: #3572e6;
}

.avatar-name {
  font-size: 20px;
  font-weight: bold;
  color: #222;
}

.info-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-group {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px #e0e0e033;
}

.info-group-title {
  font-size: 16px;
  font-weight: 600;
  color: #222;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f2f2f2;
}

.info-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f7f8fa;
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 15px;
  color: #666;
  min-width: 80px;
  margin-right: 16px;
}

.info-value {
  flex: 1;
  font-size: 15px;
  color: #222;
  text-align: right;
}

.auth-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  background: #e6f7ff;
  color: #1890ff;
  margin-left: 8px;
}

.auth-badge.unauth {
  background: #fff7e6;
  color: #fa8c16;
}

.roles-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.role-tag {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 14px;
  background: #eaf3ff;
  color: #3572e6;
}
</style>

